import { computed, ref, unref, watch } from 'vue';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';
import { PROPERTY_SECTION_KEYS } from './salesPropertyFormOptions';
import {
  defaultDeadlineForDayType,
  defaultDeadlineForPayment,
} from './salesPropertyDeadlineDefaults';
import { sectionErrors } from './salesPropertyValidation';

const clone = value => JSON.parse(JSON.stringify(value || {}));

const initialProfile = transaction => {
  const profile = clone(transaction?.profile);
  const property = transaction?.property || {};
  const address = property.address || {};
  const paymentMethods = Array.isArray(profile.forma_pagamento)
    ? profile.forma_pagamento
    : String(profile.forma_pagamento || '')
        .split(',')
        .filter(Boolean);
  return {
    ...profile,
    codigo: profile.codigo || property.code || '',
    cep: profile.cep || address.cep || '',
    logradouro: profile.logradouro || address.logradouro || '',
    cidade: profile.cidade || address.cidade || '',
    estado: profile.estado || address.estado || '',
    bairro: profile.bairro || address.bairro || '',
    valorAnunciado:
      profile.valorAnunciado || transaction?.contracted_total || '',
    valorVenda: profile.valorVenda || transaction?.contracted_total || '',
    tipo_dias: profile.tipo_dias || '1',
    prazo: profile.prazo || defaultDeadlineForPayment(paymentMethods),
    laudemios: Array.isArray(profile.laudemios) ? profile.laudemios : [],
    pendenciasImovel: profile.pendenciasImovel || {},
    forma_pagamento: paymentMethods,
  };
};

export function useSalesPropertyForm({
  id,
  transaction,
  t,
  notify,
  onSaved,
  initialSection = '',
  propertyDetailMode = false,
  sectionKeys = PROPERTY_SECTION_KEYS,
}) {
  const availableSections = computed(() => unref(sectionKeys));
  const profile = ref({});
  const resolvedInitialSection = () => {
    const requested = unref(initialSection);
    return availableSections.value.includes(requested)
      ? requested
      : availableSections.value[0];
  };
  const activeSection = ref(resolvedInitialSection());
  const errors = ref({});
  const propertyDocuments = ref({});
  const deadlineAutoManaged = ref(false);
  const saving = ref(false);
  const error = ref('');

  watch(
    transaction,
    value => {
      if (value) {
        deadlineAutoManaged.value = !String(value.profile?.prazo || '').trim();
        profile.value = initialProfile(value);
        propertyDocuments.value = value.property?.documents || {};
      }
    },
    { immediate: true }
  );

  watch(availableSections, sections => {
    if (!sections.includes(activeSection.value)) {
      activeSection.value = resolvedInitialSection();
    }
  });

  watch(
    () => unref(initialSection),
    section => {
      if (availableSections.value.includes(section)) {
        activeSection.value = section;
      }
    },
    { immediate: true }
  );

  const property = computed(() => ({
    ...(transaction.value?.property || {}),
    documents: propertyDocuments.value,
  }));
  const currentIndex = computed(() =>
    availableSections.value.indexOf(activeSection.value)
  );

  const updateProfile = value => {
    profile.value = clone(value);
    errors.value = {};
  };

  const updateField = (field, value) => {
    const nextProfile = { ...profile.value, [field]: value };
    if (field === 'prazo') deadlineAutoManaged.value = false;
    if (field === 'forma_pagamento' && deadlineAutoManaged.value) {
      const deadline = defaultDeadlineForPayment(value);
      if (deadline) nextProfile.prazo = deadline;
    }
    if (field === 'tipo_dias' && deadlineAutoManaged.value) {
      const deadline = defaultDeadlineForDayType(
        nextProfile.forma_pagamento,
        value
      );
      if (deadline) nextProfile.prazo = deadline;
    }
    profile.value = nextProfile;
    delete errors.value[field];
  };

  const updateNested = (field, key, value) => {
    profile.value = {
      ...profile.value,
      [field]: { ...(profile.value[field] || {}), [key]: value },
    };
    delete errors.value[field];
  };

  const updateDocuments = documents => {
    propertyDocuments.value = { ...documents };
    delete errors.value.documents;
  };

  const errorsFor = section =>
    sectionErrors({
      profile: profile.value,
      property: property.value,
      section,
      t,
    });

  const validateSection = section => {
    const nextErrors = errorsFor(section);
    errors.value = nextErrors;
    return Object.keys(nextErrors).length === 0;
  };

  const isSectionComplete = section =>
    Object.keys(errorsFor(section)).length === 0;

  const save = async () => {
    if (!id.value) return null;
    saving.value = true;
    error.value = '';
    try {
      if (unref(propertyDetailMode) && activeSection.value === 'documents') {
        notify(t('SALES_PROPERTY.ACTIONS.SAVED'));
        return transaction.value;
      }
      const response = unref(propertyDetailMode)
        ? await SalesTransactionsAPI.savePropertyDetail(
            id.value,
            activeSection.value,
            profile.value
          )
        : await SalesTransactionsAPI.saveDraft(id.value, profile.value);
      const saved = response?.data || null;
      if (saved) {
        transaction.value = saved;
        onSaved?.(saved);
      }
      notify(t('SALES_PROPERTY.ACTIONS.SAVED'));
      return saved;
    } catch (requestError) {
      error.value =
        requestError?.response?.data?.message ||
        t('SALES_PROPERTY.ERRORS.SAVE');
      notify(error.value);
      return null;
    } finally {
      saving.value = false;
    }
  };

  const goTo = section => {
    activeSection.value = section;
    errors.value = {};
  };

  const next = async () => {
    if (!validateSection(activeSection.value)) {
      notify(t('SALES_PROPERTY.ERRORS.SECTION'));
      return false;
    }
    const saved = await save();
    if (!saved) return false;
    const nextSection = availableSections.value[currentIndex.value + 1];
    if (nextSection) goTo(nextSection);
    return Boolean(nextSection);
  };

  const previous = () => {
    const previousSection = availableSections.value[currentIndex.value - 1];
    if (previousSection) goTo(previousSection);
    return Boolean(previousSection);
  };

  return {
    activeSection,
    currentIndex,
    error,
    errors,
    goTo,
    isSectionComplete,
    next,
    previous,
    profile,
    property,
    save,
    saving,
    updateField,
    updateDocuments,
    updateNested,
    updateProfile,
    validateSection,
  };
}
